import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { Icon } from 'semantic-ui-react';

import { useProjectWorksheetContext } from '~/features/projectWizard/worksheets/project/providers/ProjectWorksheetProvider';
import { useProjectContext } from '~/hooks';
import { knownErrors } from '~/schema/project';
import { usePageHeaderContext } from '~/components/PageHeader/PageHeaderProvider';
import FieldErrorCount from '../ProjectWorksheetHeader/FieldErrorCount';
import './styles.scss';

type KnownErrorCategories = Record<string, string[]>;

function SectionHeading({
  stickyTop,
  stickyTopPx,
  miniHeaderVisible,
  collapsed,
  onToggle,
  children
}: {
  stickyTop: string;
  stickyTopPx: number;
  miniHeaderVisible: boolean;
  collapsed: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting && entry.boundingClientRect.top <= stickyTopPx),
      { rootMargin: `-${stickyTopPx}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [stickyTopPx]);

  const className = [
    'card',
    'worksheet-body__section-heading',
    isStuck && 'is-stuck',
    isStuck && miniHeaderVisible && 'mini-header-visible'
  ].filter(Boolean).join(' ');

  return (
    <>
      <div ref={sentinelRef} className="worksheet-body__section-sentinel" />
      <div
        className={className}
        style={{ top: stickyTop }}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <Icon
          name={collapsed ? 'chevron right' : 'chevron down'}
          className="worksheet-body__section-chevron"
        />
        {children}
      </div>
    </>
  );
}

// Scrolls to the section anchor indicated by a legacy `?s=<base64 json>` query
// param or by `:section` in the path, so old wizard-style links still land in
// roughly the right place on the single-view worksheet.
function useInitialSectionScroll() {
  const { search, pathname } = useLocation();

  useEffect(() => {
    let targetId: string | undefined;

    const parsed = queryString.parse(search)?.s as string | undefined;
    if (parsed) {
      try {
        const decoded = JSON.parse(atob(decodeURIComponent(parsed)));
        if (decoded?.subsectionId) {
          targetId = `${decoded.sectionId ?? ''}-${decoded.subsectionId}`;
        } else if (decoded?.sectionId) {
          targetId = decoded.sectionId;
        }
      } catch {
        // Ignore malformed legacy state
      }
    }

    if (!targetId) {
      const pathMatch = pathname.match(/\/worksheet\/([^/?#]+)/);
      if (pathMatch) targetId = pathMatch[1];
    }

    if (!targetId) return;

    const frame = requestAnimationFrame(() => {
      const el = document.getElementById(targetId as string);
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, []);
}

export default function Worksheet() {
  const {
    config,
    isSectionCollapsed,
    toggleSectionCollapsed,
    actionBarHeight
  } = useProjectWorksheetContext();
  const {
    conditions: {
      sectionLabel,
      subsectionLabel,
      isSubsectionDisplaying
    }
  } = useProjectContext();
  const pageHeaderContext = usePageHeaderContext();
  useInitialSectionScroll();

  const stickyTopPx = (pageHeaderContext.height ?? 0) + actionBarHeight;
  const stickyTop = `${stickyTopPx / 16}rem`;
  const sectionScrollMargin = `${stickyTopPx / 16}rem`;
  const subsectionScrollMargin = `${(stickyTopPx + 64) / 16}rem`;

  return (
    <div className="worksheet-body">
      {config.map(section => {
        const subsections = section.subsections.filter(sub => isSubsectionDisplaying(sub.id));
        if (subsections.length === 0) return null;

        const categories = knownErrors[section.id] as KnownErrorCategories;
        const sectionErrorFields = Object.values(categories ?? {}).flatMap(fields =>
          Array.isArray(fields) ? fields : []
        );

        const collapsed = isSectionCollapsed(section.id);
        return (
          <section
            key={section.id}
            id={section.id}
            className="worksheet-body__section"
            style={{ scrollMarginTop: sectionScrollMargin }}
          >
            <SectionHeading
              stickyTop={stickyTop}
              stickyTopPx={stickyTopPx}
              miniHeaderVisible={pageHeaderContext.miniHeaderVisible}
              collapsed={collapsed}
              onToggle={() => toggleSectionCollapsed(section.id)}
            >
              <h2>{sectionLabel(section.id, section.label)}</h2>
              <FieldErrorCount categoryData={sectionErrorFields} showSuccessState />
            </SectionHeading>
            {!collapsed && subsections.map(sub => {
              const Component = sub.component;
              const subErrorFields = (categories?.[sub.id] as string[] | undefined) ?? [];
              return (
                <div
                  key={sub.id}
                  id={`${section.id}-${sub.id}`}
                  className="card worksheet-body__subsection"
                  style={{ scrollMarginTop: subsectionScrollMargin }}
                >
                  <div className="worksheet-body__subsection-heading">
                    <h3>{subsectionLabel(sub.id, sub.label)}</h3>
                    <FieldErrorCount categoryData={subErrorFields} showSuccessState />
                  </div>
                  <Component />
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}
